import I18n from 'i18n-js';
import React, { useContext, useState } from 'react';

import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { gql } from '@reverbdotcom/commons/src/gql';
import { RCLoadingBars } from '@reverbdotcom/cadence/components';
import { parseAmount } from '@reverbdotcom/commons/src/money';
import { useEppoExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';
import { EppoSubjectTypes } from '@reverbdotcom/commons/src/eppo/eppoExperimentHelpers';

import { SellFormContext } from '../../SellFormContext';
import { packageEntryComplete } from '../../../../discovery/shipping_labels/shipping_label_package';
import { carrierCalculatedPackageValidator } from './carrierCalculatedPackageValidator';
import { Field } from '../../sellFormReducerTypes';

export default function CarrierCalculatedEstimator() {
  const { listingEntry, shopConfig } = useContext(SellFormContext);
  const [shippingEstimateResponse, setShippingEstimateResponse] = useState(null);
  const user = useUser();
  const inExperiment = useEppoExpEnabled({
    flagKey: eppoFlags.SHIPPING_RANGE_ESTIMATE,
    subjectKey: user.id,
    subjectType: EppoSubjectTypes.USER_ID,
  });

  const packageEntry = listingEntry[Field.CARRIER_CALCULATED_PACKAGE];
  const validPackageEntry = packageEntryComplete(packageEntry) && !carrierCalculatedPackageValidator(packageEntry)?.errorTranslationKey;
  const currentPriceInput = listingEntry[Field.PRICE]?.amount ?? '';
  const currentPriceCents = parseAmount(currentPriceInput, shopConfig.currencySetting).amountCents;
  const shouldFetchEstimate = shopConfig.currencySetting === 'USD' &&
                                validPackageEntry &&
                                !!currentPriceInput &&
                                currentPriceCents > 0;

  const { loading } = useQuery(
    FETCH_SHIPPING_ESTIMATE,
    {
      skip: !shouldFetchEstimate,
      variables: {
        input: {
          packageEntry,
          itemPrice: {
            amount: currentPriceInput,
            currency: shopConfig.currencySetting,
          },
          categoryRootUuid: listingEntry[Field.ROOT_CATEGORY_UUID],
          categoryUuids: listingEntry[Field.SUBCATEGORY_LEAF_UUIDS],
        },
      },
      onCompleted: ({ shippingEstimate }) => setShippingEstimateResponse(shippingEstimate),
    },
  );

  const estimateAvailable = !loading && shippingEstimateResponse?.priceLow?.display;

  return (
    <>
      {inExperiment && loading && (
        <div className="d-flex fx-align-center fx-justify-center mt-6 mb-2">
          <RCLoadingBars />
        </div>
      )}

      {inExperiment && estimateAvailable && (
        <div className="d-flex fx-dir-row mobile-fx-dir-col mt-6">
          <div className="mr-2 mobile-mb-2">
            {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostHintV2')}
          </div>
          <div>
            <strong>
              {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostRange', {
                lowDisplay: shippingEstimateResponse.priceLow.display,
                highDisplay: shippingEstimateResponse.priceHigh.display,
              })}
            </strong>
          </div>
        </div>
      )}
    </>
  );
}

export const FETCH_SHIPPING_ESTIMATE = gql(`
  query Core_SellForm_FetchShippingEstimate($input: InputShippingEstimateRequest!) {
    shippingEstimate(input: $input) {
      priceLow {
        amountCents
        display
      }
      priceHigh {
        amountCents
        display
      }
      __typename
    }
  },
`);
